import { DomPortal } from '@angular/cdk/portal';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Provinces, StateProvinces } from '../../config/state-provinces';
import { FullAddress } from '../../models/address';
import { User } from '../../models/user';
import { AddressService } from '../../services/address.service';
import { ErrorService } from '../../services/error.service';
import { MapService } from '../../services/map.service';
import { SnackbarService } from '../../services/snackbar.service';
import { SubscriptionService } from '../../services/subscription.service';
import { ModalComponent } from '../modal/modal.component';
import { UntypedFormGroup } from '@angular/forms';
import { Validators } from '@angular/forms';
import { SpinnerService } from '../../services/spinner.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
	selector: 'app-address-modal',
	templateUrl: './address-modal.html'
})

export class AddressModalComponent implements OnInit, OnDestroy {
	public addressBookAddress: boolean;
	public error;
	public invalidAddress;
	public loading;
	public mapPortal: DomPortal<any>;
	public mapVal;
	public modalAddress: FullAddress = new FullAddress();
	public noResults: boolean;
	public originalModalAddress: FullAddress = new FullAddress();
	public requireValidation;
	public selectValidAddress: boolean;
	public selectedAddress;
	public showDraggableCheckbox: boolean;
	public showDraggableMap: boolean;
	public stateProvinces: StateProvinces = new StateProvinces();
	public states: Provinces = new Provinces();
	public submittedAddress;
	public subscriptions: any[] = [];
	public type;
	public useAddressAsEntered: boolean;
	public user: User = new User();
	public validationResults;
	public warning;
	public validationWarningMessage = 'addressModalView.validationWarningMessage';

	constructor(private _http: HttpClient,
				private _vcRef: ViewContainerRef,
				private _addressService: AddressService,
				private _errorService: ErrorService,
				public dialogRef: MatDialogRef<AddressModalComponent>,
				@Inject(MAT_DIALOG_DATA) public data: any,
				private _dialog: MatDialog,
				private _snackbarService: SnackbarService,
				private _mapService: MapService,
				private _subscriptionService: SubscriptionService,
				private _spinnerService: SpinnerService,
				private _translateService: TranslateService) {
		this.user = data.user;
		this.useAddressAsEntered = false;
		this.showDraggableMap = false;
		this.selectValidAddress = false;
		this.noResults = false;
		if (data.address) {
			this.modalAddress = Object.assign({}, data.address);
			if (this.modalAddress.country === 'CA' && this.modalAddress.nonUsAddress === 1) {
				// Sometimes we get split zip sometimes not so if no plus 4 just use zip
				this.modalAddress.country = 'CA';
				if (this.modalAddress?.plus4) {
					this.modalAddress.postalCode = this.modalAddress?.zip?.substring(0, 3) + " " + this.modalAddress?.plus4?.trim();
				} else {
					this.modalAddress.postalCode = this.modalAddress?.zip;
				}
			}
			else if (this.modalAddress.country === 'OTHER' && this.modalAddress.nonUsAddress === 2) {
				// Sometimes we get split zip sometimes not so if no plus 4 just use zip
				this.modalAddress.country = 'OTHER';
				this.modalAddress.postalCode = this.modalAddress?.zip;
			}
			else if (this.modalAddress.country === 'US' && this.modalAddress.nonUsAddress === 0) {
				if (this.modalAddress?.postalCode) {
					this.modalAddress.postalCode = null;
				}
			}
		} else {
			if (this.user.globalSettings.useCanadianAddressesByDefault === true) {
				this.modalAddress.country = 'CA';
			} else {
				this.modalAddress.country = 'US';
			}
		}
		if (data.name) {
			this.modalAddress.name = data.name;
		}
		this.requireValidation = this.user.validateInetAddresses;
		if (data.type !== undefined) {
			this.type = data.type;
			this.addressBookAddress = false;
		} else {
			this.addressBookAddress = true;
		}
		if (this.data.validationResults && this.data.validationResults.length > 0) {
			this.validationResults = this.data.validationResults;
			this.submittedAddress = this.data.address;
			this.modalAddress = Object.assign({}, data.address);
			this.originalModalAddress = { ...this.modalAddress };
			this.invalidAddress = true;
			this.selectValidAddress = true;
			// This is used to show the checkbox where showDraggableMap is used to show the map
			if (this.user.allowMapValidation) {
				this.showDraggableCheckbox = true;
			}
			this.selectedAddress = this.validationResults[0].id;
			const address: FullAddress = new FullAddress();
			address.address1 = this.validationResults[0].streetAddress1;
			address.address2 = this.validationResults[0].streetAddress2;
			address.city = this.validationResults[0].city;
			address.state = this.validationResults[0].province;
			address.zip = this.validationResults[0].postalCode;
			address.id = this.validationResults[0].id;
			address.lat = this.validationResults[0].geocode?.latitude ??address.lat;
			address.lon = this.validationResults[0].geocode?.longitude ?? address.lon;
			if (this.type) {
				this.initConfirmMap(address);
			}
			setTimeout(() => {
				this.setValidatedAddress(this.modalAddress, this.validationResults[0], false);
			});
		}
		this.warning = data.warning;
		this.states.stateProvinces = [];
		this.error = null;
		this._http.get<any>('./app/config/states-and-provinces.json')
			.subscribe(
				(response) => {
					this.stateProvinces = response;
					this.setStatesGroup(this.modalAddress.country, null);
				},
				(error) => {
					this._errorService.error(true, error, 'Get states-and-provinces failed');
				});

		this.setGoogleAddress = this.setGoogleAddress.bind(this);
	}

	checkValidationResponse(response) {
		if (response.length === 1 &&
			(
				response[0].streetAddress1 === this.modalAddress.address1 &&
				response[0].city === this.modalAddress.city &&
				response[0].province === this.modalAddress.state &&
				response[0].postalCode === this.modalAddress.zip &&
				response[0].geocode &&
				response[0].geocode.latitude &&
				response[0].geocode.longitude
			)
		) {
			this.setValidatedAddress(this.modalAddress, response[0], false);
			return true;
		} else {
			return false;
		}
	}

	clearValidationResults() {
		this.validationResults = undefined;
		this.invalidAddress = false;
		if (this.type && this.modalAddress.id) {
			delete this.modalAddress.id;
		}
	}

	closeModal() {
		// Normal cancel behavior - just close the modal without any action
		this.dialogRef.close();
	}

	// Check if we should show the "Remove Invalid Address" button
	shouldShowRemoveButton(): boolean {
		// XD-52722: Use enforcement decision passed from order-entry component to ensure
		// consistency with disableClose behavior and avoid logic duplication
		return this.data?.enforcementApplies || false;
	}

	removeInvalidAddress() {
		// Show warning confirmation using existing ModalComponent pattern
		const translatedMessage = this._translateService.instant(this.validationWarningMessage);
		const warningDialog = this._dialog.open(ModalComponent, {
			data: {
				title: this._translateService.instant('addressModalView.warningText'),
				message: translatedMessage,
				showCancel: true,
				cancelText: this._translateService.instant('addressModalView.cancelButton'),
				okText: this._translateService.instant('addressModalView.proceedButton')
			}
		});

		warningDialog.afterClosed().subscribe((result) => {
			if (result) {
				// User clicked Proceed - close this modal with signal, then closeAll will be handled by parent
				this.dialogRef.close({ action: 'removeAddress', addressType: this.type });
			}
			// If Cancel, do nothing - stay on current address modal
		});
	}


	editAddress() {
		if (this.noResults) {
			this.validationResults = false;
			this.noResults = false;
		} else {
			this.validationResults = !this.validationResults;
		}
		this.modalAddress = this.submittedAddress;
	}

	initConfirmMap(address, draggable?) {
		this._mapService.markerUpdated$.next({ address: null, type: 'ORIGIN' });
		this._mapService.markerUpdated$.next({ address: null, type: 'DESTINATION' });
		this._mapService.markerUpdated$.next({ address, type: 'ORIGIN', draggable: draggable, options: { streetViewControl: true, mapTypeControl: true, fullscreenControl: false } });
	}

	initDraggableMap(event) {
		this.showDraggableMap = event;
		if (this.showDraggableMap) {
			this.initConfirmMap(this.modalAddress, true);
		} else {
			this.initConfirmMap(this.modalAddress);
		}
	}

	ngOnDestroy(): void {
		this._subscriptionService.unSubscribe(this.subscriptions);
		// Clear out any markers
		this._mapService.markerUpdated$.next({ address: null, type: 'ORIGIN' });
	}

	ngOnInit(): void {
		this.mapPortal = new DomPortal(document?.querySelector('#mapPortalContent'));
		this.subscriptions.push(this._mapService.markerLocationChanged$.subscribe((location) => {
			// This is kinda dumb but is how it worked before, if the user drops the pin it sets the address without confirmation of what address will be used
			this.mapVal = location;
			this.setValidatedAddress(this.modalAddress, this.mapVal, true);
		}));
	}

	phoneNumberMask(userInput: string, country: string) {
		if(!userInput){
			userInput = '';
		}
		const numbers = userInput.match(/\d/g) || [];
		let numberLength = numbers.join('').length;
		if (country === 'US' || country === 'CA') {
			if (numberLength > 10) {
				return ['(', /[0-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, ' ', 'x', /\d/, /\d/, /\d/, /\d/, /\d/];
			} else {
				return ['(', /[0-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
			}
		} else {
			// Bypass validation for other countries
			return false;
		}
	}

	saveAddress() {
		// Reset the error message
		this.error = null;
		// Set address block values that are expected on the server
		this.modalAddress.userId = this.user.primaryCustomerId;
		if (this.modalAddress.country === 'US') {
			this.modalAddress.nonUsAddress = 0;
			this.modalAddress.postalCode = null;
		} else if (this.modalAddress.country === 'CA') {
			this.modalAddress.nonUsAddress = 1;
			this.modalAddress.plus4 = null;
		}
		else{
			this.modalAddress.nonUsAddress = 2;
			this.modalAddress.plus4 = null;
			this.modalAddress.postalCode = null;
			this.modalAddress.zip = null;
		}

		const returnObject = {
			address: null,
			type: null
		};
		this.modalAddress.userId = this.modalAddress.userId.toString();

		// Determine if the address is an edit or a new save
		if (!this.modalAddress.id) {
			// Save new address
			if (!this.type) {
				this._spinnerService.startSpinner();
				this._addressService.createAddress(this.modalAddress, false)
					.subscribe(
						(response) => {
							this._spinnerService.stopSpinner();
							returnObject.address = response;
							if (this.type) {
								returnObject.type = this.type;
							}
							this.dialogRef.close(returnObject);
						},
						(error) => {
							this._spinnerService.stopSpinner();
							if (error.status !== 409) {
								this._errorService.error(true, error, 'Duplicate Address Modal failed');
							} else {
								const dialogRef = this._dialog.open(ModalComponent, {
									data: {
										title: 'Warning',
										message: 'A similar address point already exists. Are you sure you want to save a duplicate address point?',
										showCancel: true,
										cancelText: 'No',
										okText: 'Yes'
									}
								});
								dialogRef.afterClosed().subscribe((result) => {
									if (result) {
										this._addressService.createAddress(this.modalAddress, true).subscribe(
											(response) => {
												returnObject.address = response;
												if (this.type) {
													returnObject.type = this.type;
												}
												this.dialogRef.close(returnObject);
											},
											(createAddressError) => {
												this._errorService.error(true, createAddressError, 'createAddress failed');
											}
										);
									}
								});
							}
						}
					);
			} else {
				returnObject.address = this.modalAddress;
				returnObject.type = this.type;
				this.dialogRef.close(returnObject);
			}

		} else {
			// Edit existing address
			if (!this.modalAddress.phoneAsList) {
				this.modalAddress.phoneAsList = [];
			}
			if (!this.type) {
				this._spinnerService.startSpinner();
				this._addressService.updateAddress(
					this.modalAddress,
					this.modalAddress.id
				)
					.subscribe(
						(response) => {
							this._spinnerService.stopSpinner();
							returnObject.address = response;
							if (this.type) {
								returnObject.type = this.type;
							}
							this.dialogRef.close(returnObject);
						},
						(error) => {
							this._spinnerService.stopSpinner();
							this._errorService.error(true, error, 'updateAddress failed');
						}
					);
			} else {
				returnObject.address = this.modalAddress;
				returnObject.type = this.type;
				this.dialogRef.close(returnObject);
			}

		}

	}

	setGoogleAddress(e, form) {
		if (e.geometry) {
			// Set up a template for the google address to fill out with all required fields
			const g = {
				subpremise: '',
				street_number: '',
				route: '',
				locality: '',
				administrative_area_level_1: '',
				country: '',
				postal_code: '',
				postal_code_suffix: '',
				postal_code_prefix: '',
				name: '',
				subpremise_full: '',
				street_number_full: '',
				route_full: '',
				locality_full: '',
				administrative_area_level_1_full: '',
				country_full: '',
				postal_code_full: '',
				postal_code_suffix_full: '',
				postal_code_prefix_full: '',
				name_full: ''
			};
			// Loop through the google source address and apply all field values to corresponding values in the template g
			for (let i = 0; i < e.address_components.length; i++) {
				g[e.address_components[i].types[0]] = e.address_components[i].short_name;
				g[e.address_components[i].types[0] + '_full'] = e.address_components[i].long_name;
			}

			const countryCode = g.country;
			this.modalAddress.nonUsAddress = g.country === 'CA' ? 1 : ((g.country === 'US')?0 : 2);
			this.modalAddress.country = (g.country === 'US' || g.country === 'CA')? countryCode: 'OTHER';
			form.controls.country.markAsDirty();
			this.setStatesGroup(this.modalAddress.country, form);
			const lat = e.geometry.location.lat();
			const lng = e.geometry.location.lng();
			// Take newly mapped values in our google template and apply them to the correctly named fields in our new address
			this.modalAddress.address1 = g.street_number + ' ' + g.route;
			form.controls.address1.markAsDirty();
			this.modalAddress.address2 = g.subpremise;
			form.controls.address2.markAsDirty();
			if(this.modalAddress.country !== 'OTHER'){
				this.modalAddress.city = g.locality;
			}
			else{
				this.modalAddress.city = g.locality + ', ' + g.administrative_area_level_1 + ', ' + g.country_full + ' - ' + g.postal_code;
			}
			form.controls.city.markAsDirty();
			if(this.modalAddress.country !== 'OTHER'){
				this.modalAddress.state = g.administrative_area_level_1;
				form.controls.state.markAsDirty();
			}
			if (this.modalAddress.country === 'US') {
				if (g.postal_code_suffix?.length > 0) {
					this.modalAddress.plus4 = g.postal_code_suffix;
					if (form.controls.plus4) {
						form.controls.plus4.markAsDirty();
					}
				} else {
					this.modalAddress.plus4 = null;
					form.controls.plus4?.reset();
				}
				this.modalAddress.zip = g.postal_code;
				form.controls.zip?.markAsDirty();
			}
			else if (this.modalAddress.country === 'CA') {
				this.modalAddress.postalCode = g.postal_code;
				form.controls.postalCode?.markAsDirty();
			}
			else if (this.modalAddress.country === 'OTHER') {
				this.modalAddress.postalCode = g.postal_code;
			}
			this.modalAddress.name = e.name;
			form.controls.name.markAsDirty();

			if (e.formatted_phone_number) {
				this.modalAddress.phone = e.formatted_phone_number;
			}
			if (lat && lng) {
				this.modalAddress.lat = lat;
				this.modalAddress.lon = lng;
				this.modalAddress.validated = 9;
			}
		}
	}

	setStatesGroup(value: string, form: UntypedFormGroup) {
		if (value === 'CA') {
			this.states = this.stateProvinces.canadianProvinces;
		} else if (value === 'US') {
			this.states = this.stateProvinces.states;
		} else if (value === 'OTHER') {
			this.states = null;
		}
		if (form) {
			this.setValidators(form, value);
		}
	}

	setValidators(form: any, country: string) {
		if (country === 'US' || country === 'CA') {
			if (form.controls['address1']) {
				form.controls['address1'].setValidators([Validators.required, Validators.pattern('.*[^ ].*')]);
			}
			if (form.controls['city']) {
				form.controls['city'].setValidators([Validators.required, Validators.pattern('.*[^ ].*')]);
			}
			if (form.controls['state']) {
				form.controls['state'].setValidators([Validators.required]);
			}
			if (country === 'US') {
				if (form.controls['zip']) {
					form.controls['zip'].setValidators([Validators.required, Validators.maxLength(5)]);
				}
			} else if (country === 'CA') {
				if (form.controls['postalCode']) {
					form.controls['postalCode'].setValidators([Validators.required, Validators.maxLength(7)]);
				}
			}
		} else if (country === 'OTHER') {
			form.controls['lat'] = '';
			form.controls['lon'] = '';
			this.modalAddress.lat = '';
			this.modalAddress.lon = '';
			this.clearValidators(form);
			if(this.user.globalSettings.requirePhoneNumberClientPortal){
				form.controls['phone'].setValidators([Validators.required]);
			}
			if (form.controls['name']) {
				form.controls['name'].setValidators([Validators.required, Validators.pattern('.*[^ ].*')]);
			}
		}

		if (form && (typeof form.updateValueAndValidity === 'function')) {
			form.updateValueAndValidity();
		}
	}

	clearValidators(form: any) {
		for (const control in form.controls) {
			if (form.controls.hasOwnProperty(control)) {
				if (typeof form.controls[control].clearValidators === 'function') {
					form.controls[control].clearValidators();
				}
				if (typeof form.controls[control].updateValueAndValidity === 'function') {
					form.controls[control].updateValueAndValidity();
				}
			}
		}
	}

	setValidatedAddress(base, valid, dragged) {
		if (!dragged) {
			this.useAddressAsEntered = false;
			this.showDraggableMap = false;
			base.address1 = valid.streetAddress1;
			base.city = valid.city;
			base.state = valid.province;
			base.zip = valid.postalCode;
			if (valid.geocode) {
				base.lat = valid.geocode.latitude;
				base.lon = valid.geocode.longitude;
				base.validated = 9;
				this.invalidAddress = false;
			}
			if (valid.countryCode === 'CA') {
				base.nonUsAddress = 1;
			} else if (valid.countryCode === 'US'){
				base.nonUsAddress = 0;
			} else {
				base.nonUsAddress = 2;
			}
			base.country = valid.countryCode;

			if (!this.type) {
				this.initConfirmMap(base);
			}
		} else {
			// Set up a template for the google address to fill out with all required fields
			const g = {
				subpremise: '',
				street_number: '',
				route: '',
				locality: '',
				administrative_area_level_1: '',
				country: '',
				postal_code: '',
				postal_code_suffix: '',
				postal_code_prefix: '',
				name: ''
			};
			// Loop through the google source address and apply all field values to corresponding values in the template g
			for (let i = 0; i < valid.address_components.length; i++) {
				g[valid.address_components[i].types[0]] = valid.address_components[i].short_name;
			}
			// Take newly mapped values in our google template and apply them to the correctly named fields in our new address
			base.address1 = g.street_number + ' ' + g.route;
			base.address2 = g.subpremise;
			base.city = g.locality;
			base.state = g.administrative_area_level_1;
			base.zip = g.postal_code_suffix;
			base.lat = this.mapVal.geometry.location.lat();
			base.lon = this.mapVal.geometry.location.lng();
			base.validated = 8;
		}

	}

	useSubmittedAddress() {
		this.modalAddress = Object.assign({}, this.submittedAddress);
		this.useAddressAsEntered = true;
		this.initConfirmMap(this.modalAddress);
	}

	validateAddress(event, form) {
		const formIsInvalid = (!form.valid || (this.noResults && this.requireValidation));
		if (!formIsInvalid) {
			if (event) {
				event.target.blur();
			}
			// Making a copy of modalAddress as modalAddress can be updated but then the user may want to change their mind.
			this.originalModalAddress = { ...this.modalAddress };
			// If the user is editing an address from anywhere except address book and they have dirtied the form then we must remove the address id and address validation
			if (!this.data.fromAddressBook && form.dirty) {
				this.modalAddress.id = null;
			}
			if (this.modalAddress.country === 'OTHER') {
				this.saveAddress();
				return;
			}
			if (!this.requireValidation && Array.isArray(this.validationResults) && this.validationResults.length === 0) {
				this.modalAddress.lat = null;
				this.modalAddress.lon = null;
				this.modalAddress.validated = 0;
			}
			if (!this.validationResults && !this.noResults) {
				this.loading = true;
				this.modalAddress.lat = null;
				this.modalAddress.lon = null;
				this.modalAddress.validated = 0;
				this.submittedAddress = Object.assign({}, this.modalAddress);

				const countryCode = this.modalAddress.country;
				this._addressService.validateAddress(
					this.modalAddress.address1,
					this.modalAddress.city,
					this.modalAddress.state,
					(this.modalAddress.country === 'CA' || this.modalAddress.country === 'OTHER') ? this.modalAddress.postalCode : this.modalAddress.zip,
					countryCode
				).subscribe(
					response => {
						this.loading = false;
						if (response.validationResults && response.validationResults.length === 0) {
							this.modalAddress.bypassValidation = true;
							if (this.user.validateInetAddresses) {
								this.noResults = true;
							} else {
								if (this.user.allowMapValidation) {
									this.noResults = true;
									this.initDraggableMap(true);
								} else {
									this.noResults = true;
									this.selectedAddress = this.submittedAddress;
								}
							}
						} else {
							for (const i of Object.keys(response.validationResults)) {
								response.validationResults[i].id = i;
							}
							if (!this.checkValidationResponse(response.validationResults) && response.validationResults.length > 0) {
								this.validationResults = response.validationResults;
								// this removes the action of selected the correct address... bad user experience imo
								this.invalidAddress = true;
								this.selectValidAddress = true;
								// This is used to show the checkbox where showDraggableMap is used to show the map
								if (this.user.allowMapValidation) {
									this.showDraggableCheckbox = true;
								}
								if (this.validationResults[0].id && this.validationResults[0].geocode) {
									this.selectedAddress = this.validationResults[0].id;
									const address: FullAddress = new FullAddress();
									address.address1 = this.validationResults[0].streetAddress1;
									address.address2 = this.validationResults[0].streetAddress2;
									address.city = this.validationResults[0].city;
									address.state = this.validationResults[0].province;
									address.zip = this.validationResults[0].postalCode;
									address.id = this.validationResults[0].id;
									address.lat = this.validationResults[0].geocode?.latitude ??address.lat;
									address.lon = this.validationResults[0].geocode?.longitude ?? address.lon;
									if (this.type) {
										this.initConfirmMap(address);
									}
									this.setValidatedAddress(this.modalAddress, this.validationResults[0], false);
								}
							} else if (this.checkValidationResponse(response.validationResults) && response.validationResults.length > 0 && this.addressBookAddress) {
								this.validationResults = response.validationResults;
								this.invalidAddress = false;
								this.selectValidAddress = false;
								const address: FullAddress = new FullAddress();
								address.address1 = this.validationResults[0].streetAddress1;
								address.address2 = this.validationResults[0].streetAddress2;
								address.city = this.validationResults[0].city;
								address.state = this.validationResults[0].province;
								address.zip = this.validationResults[0].postalCode;
								address.id = this.validationResults[0].id;
								address.lat = this.validationResults[0].geocode?.latitude ??address.lat;
								address.lon = this.validationResults[0].geocode?.longitude ?? address.lon;
								if (this.type) {
									this.initConfirmMap(address);
								}
								this.setValidatedAddress(this.modalAddress, this.validationResults[0], false);
								document.getElementById('btnSave').blur();
							} else {
								this._snackbarService.openSnackBar(`Address validated successfully!`, 'Close', 5000, 'success');
								this.saveAddress();
							}
						}
					},
					error => {
						this.loading = false;
						this._errorService.error(true, error, 'validation error');
					});
			} else {
				// If validation results are available and the address is being saved, we now assume the address is verified by the user
				// and no longer needs to be re-validated if they manually used an address that googles says is wrong
				if (this.validationResults) {
					this.modalAddress.bypassValidation = true;
				}
				this.saveAddress();
			}
		} else {
			this._snackbarService.openSnackBar('Please fill out all required fields.', 'Close', 3000);
		}
	}

	clearForm(form: UntypedFormGroup) {
		this.modalAddress.address1 = null;
		this.modalAddress.address2 = null;
		this.modalAddress.name = null;
		this.modalAddress.city = null;
		this.modalAddress.state = null;
		this.modalAddress.zip = null;
		this.modalAddress.plus4 = null;
		this.modalAddress.postalCode = null;

		form.controls.name.reset();
		form.controls.address1.reset();
		form.controls.city.reset();
		form.controls.state.reset();
	}
}
